import { prettifySubAccount, useSubAccounts } from '@src/providers';
import type { DealFixedRateLoanData, DealFloatingRateLoanData, DealRepoData, OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isRepoDeal } from '@src/types/OTC/otcGuards';
import {
  getOtcDealDisplayUser,
  getOtcDealRateDisplayValue,
  getOtcDealSpreadDisplayValue,
} from '@src/utils/OTC/otcDealDisplayUtils';
import { formatProductType } from '@src/utils/OTC/otcUtils';
import { prettifyAllocations } from '@src/utils/allocation';
import {
  CopyButton,
  Dd,
  DealStatus,
  Divider,
  Dl,
  Dt,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  InlineFormattedNumber,
  LoaderSizes,
  LoaderTalos,
  Text,
  Tooltip,
  abbreviateId,
  formattedDate,
  isFixedRateLoanSecurity,
  isFloatingRateLoanSecurity,
  isOTCRepoSecurity,
  prettyName,
  useElementSize,
  useMarketAccountsContext,
  useSecuritiesContext,
  type OTCFixedRateLoanSecurity,
  type OTCFloatingRateLoanSecurity,
  type OTCRepoSecurity,
  type Security,
} from '@talos/kyoko';
import type { ComponentProps, ReactNode } from 'react';
import { useDeal } from './useDeal';

const DETAILS_BREAKPOINT_PX = 1000;
const UNABLE_TO_LOAD_DEAL_MESSAGE =
  'Unable to load deal information. Please close and reopen the panel, or contact support if the issue persists.';

type LoanDeal = DealFixedRateLoanData | DealFloatingRateLoanData;
type CashflowsHeaderDeal = LoanDeal | DealRepoData;
type LoanSecurity = OTCFixedRateLoanSecurity | OTCFloatingRateLoanSecurity;

type CashflowsHeaderDisplay = {
  productType: string;
  symbol: string;
  directionLabel: string;
  directionColor: 'colorTextPositive' | 'colorTextNegative';
  amount: string | undefined;
  amountAsset: string;
  amountTestId: string;
  rateTerms: string;
  collateralAsset: string;
  assetLabel: string;
};

export function DealCashflowsHeader({ dealID, panelID }: { dealID: string; panelID: string }) {
  const { deal, isLoading } = useDeal({ dealID, panelID });

  if (isCashflowsHeaderDeal(deal)) {
    return <DealCashflowsHeaderContent deal={deal} />;
  }

  return (
    <Flex background="colors.gray.030" flex="0 0 auto" overflow="hidden" w="100%">
      <HStack
        h="64px"
        w="100%"
        p="spacing12"
        gap="spacing16"
        justifyContent="center"
        overflow="auto hidden"
        data-testid="deal-cashflows-header"
      >
        {isLoading ? (
          <LoaderTalos size={LoaderSizes.SMALL} delay={0} dataTestID="deal-cashflows-header-loader" />
        ) : (
          <Text color="colorTextSecondary" size="fontSizeDefault" data-testid="deal-cashflows-header-message">
            {UNABLE_TO_LOAD_DEAL_MESSAGE}
          </Text>
        )}
      </HStack>
    </Flex>
  );
}

function DealCashflowsHeaderContent({ deal }: { deal: CashflowsHeaderDeal }) {
  const { elementRef, size } = useElementSize<HTMLDivElement>({
    thresholds: [['offsetWidth', [DETAILS_BREAKPOINT_PX]]],
  });
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { subAccountsByName } = useSubAccounts();
  const display = getCashflowsHeaderDisplay(deal, securitiesBySymbol);
  const showSecondaryDetails = size.offsetWidth != null && size.offsetWidth >= DETAILS_BREAKPOINT_PX;
  const subAccountDisplay = getSubAccountDisplay(deal, subAccountsByName);
  const marketAccountDisplay = marketAccountsByName.get(deal.MarketAccount)?.DisplayName ?? deal.MarketAccount;

  return (
    <Flex ref={elementRef} background="colors.gray.030" flex="0 0 auto" overflow="hidden" w="100%">
      <HStack
        h="64px"
        w="100%"
        p="spacing12"
        gap="spacing16"
        justifyContent="flex-start"
        overflow="auto hidden"
        data-testid="deal-cashflows-header"
      >
        <HStack gap="spacing8" justifyContent="flex-start">
          <HStack h="28px" w="28px" borderRadius="100%" background="colors.gray.040">
            <Icon icon={IconName.Hashtag} size="fontSizeLarge" />
          </HStack>
          <HeaderItem title={<IndicatorBadge noWrap>ID</IndicatorBadge>} testid="deal-cashflows-header-id">
            <Text color="colorTextImportant" size="fontSizeDefault" weight="fontWeightMedium">
              {abbreviateId(deal.ID)}
            </Text>
          </HeaderItem>
        </HStack>

        <Divider orientation="vertical" my="spacing4" />

        <HeaderItem title={<IndicatorBadge noWrap>Product</IndicatorBadge>} testid="deal-cashflows-header-product">
          <Text color="colorTextImportant" size="fontSizeDefault" weight="fontWeightMedium">
            {display.productType}
          </Text>
        </HeaderItem>

        <Divider orientation="vertical" my="spacing4" />

        <HeaderItem
          title={
            <HStack gap="spacing16" justifyContent="flex-start">
              <IndicatorBadge noWrap>Deal</IndicatorBadge>
              <Text color="colorTextSecondary">{formattedDate(deal.SubmitTime)}</Text>
            </HStack>
          }
          testid="deal-cashflows-header-summary"
        >
          <HStack gap="spacing8" fontSize="fontSizeDefault" fontWeight="fontWeightMedium">
            <Text color="colorTextImportant">{display.symbol}</Text>
            <Text
              color={display.directionColor}
              weight="fontWeightMedium"
              transform="uppercase"
              data-testid="deal-cashflows-header-side"
            >
              {display.directionLabel}
            </Text>
            <Text weight="fontWeightBold" data-testid={display.amountTestId}>
              <InlineFormattedNumber
                number={display.amount}
                currency={display.amountAsset}
                highlightColor="colorTextImportant"
              />
            </Text>
            <Text color="colorTextImportant">{display.rateTerms}</Text>
            <Text color="colorTextImportant">
              <Text as="span" weight="fontWeightMedium" data-testid="deal-cashflows-header-collateral-asset">
                {display.collateralAsset}
              </Text>{' '}
              <Text as="span" weight="fontWeightRegular" data-testid="deal-cashflows-header-collateral-label">
                {display.assetLabel}
              </Text>
            </Text>
            <Tooltip tooltip="Copy deal ID">
              <CopyButton
                aria-label="Copy deal ID"
                data-testid="deal-cashflows-header-copy-id"
                size={FormControlSizes.Tiny}
                value={deal.ID}
              />
            </Tooltip>
          </HStack>
        </HeaderItem>

        <Divider orientation="vertical" my="spacing4" />

        <HeaderItem
          title={<IndicatorBadge noWrap>Market Account</IndicatorBadge>}
          testid="deal-cashflows-header-market-account"
        >
          <Text color="colorTextImportant" weight="fontWeightMedium">
            {marketAccountDisplay || '—'}
          </Text>
        </HeaderItem>

        {showSecondaryDetails && (
          <>
            <Divider orientation="vertical" my="spacing4" />

            <HeaderItem title={<IndicatorBadge noWrap>User</IndicatorBadge>} testid="deal-cashflows-header-user">
              <Text color="colorTextImportant" weight="fontWeightMedium">
                {getOtcDealDisplayUser(deal) ?? '—'}
              </Text>
            </HeaderItem>

            <Divider orientation="vertical" my="spacing4" />

            <Tooltip
              tooltip={subAccountDisplay}
              tooltipTestID="deal-cashflows-header-sub-accounts-tooltip"
              disabled={subAccountDisplay === '—'}
            >
              <HeaderItem
                title={<IndicatorBadge noWrap>Sub Account(s)</IndicatorBadge>}
                testid="deal-cashflows-header-sub-accounts"
                maxWidth="180px"
              >
                <Text
                  color="colorTextImportant"
                  weight="fontWeightMedium"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {subAccountDisplay}
                </Text>
              </HeaderItem>
            </Tooltip>
          </>
        )}

        <Divider orientation="vertical" my="spacing4" />

        <HeaderItem title={<IndicatorBadge noWrap>Status</IndicatorBadge>} testid="deal-cashflows-header-status">
          <DealStatus status={deal.Status} align="left" />
        </HeaderItem>
      </HStack>
    </Flex>
  );
}

function HeaderItem({
  title,
  children,
  testid,
  ...props
}: {
  title: ReactNode;
  children: ReactNode;
  testid: string;
} & Omit<ComponentProps<typeof Dl>, 'children' | 'title'>) {
  return (
    <Dl alignItems="stretch" data-testid={testid} {...props}>
      <Dt fontSize="fontSizeTiny">{title}</Dt>
      <Dd mt="spacing4" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
        {children}
      </Dd>
    </Dl>
  );
}

function isCashflowsHeaderDeal(deal: OTCDealData | undefined): deal is CashflowsHeaderDeal {
  return deal != null && (isLoanDeal(deal) || isRepoDeal(deal));
}

function isLoanDeal(deal: OTCDealData): deal is LoanDeal {
  return isFixedRateLoanDeal(deal) || isFloatingRateLoanDeal(deal);
}

function resolveLoanSecurity(deal: LoanDeal, securitiesBySymbol: Map<string, Security>): LoanSecurity | undefined {
  const security = securitiesBySymbol.get(deal.Symbol);
  return isFixedRateLoanSecurity(security) || isFloatingRateLoanSecurity(security) ? security : undefined;
}

function resolveRepoSecurity(
  deal: DealRepoData,
  securitiesBySymbol: Map<string, Security>
): OTCRepoSecurity | undefined {
  const security = securitiesBySymbol.get(deal.Symbol);
  return isOTCRepoSecurity(security) ? security : undefined;
}

function getCashflowsHeaderDisplay(
  deal: CashflowsHeaderDeal,
  securitiesBySymbol: Map<string, Security>
): CashflowsHeaderDisplay {
  if (isRepoDeal(deal)) {
    return getRepoCashflowsHeaderDisplay(deal, resolveRepoSecurity(deal, securitiesBySymbol));
  }
  return getLoanCashflowsHeaderDisplay(deal, resolveLoanSecurity(deal, securitiesBySymbol));
}

function getLoanCashflowsHeaderDisplay(deal: LoanDeal, security: LoanSecurity | undefined): CashflowsHeaderDisplay {
  const borrowedAsset =
    security?.ProductDetails.BorrowedAsset || deal.DealInfo.EnteredQuantityAsset || deal.QuantityAsset;
  const collateralAsset = security?.ProductDetails.CollateralAsset || deal.DealInfo.CollateralAsset || '—';
  const rateTerms = isFixedRateLoanDeal(deal)
    ? getFixedRateTerms(deal)
    : getFloatingRateTerms(deal, isFloatingRateLoanSecurity(security) ? security : undefined);
  const side = deal.DealInfo.Side;

  return {
    productType: formatProductType(deal.SubProductType),
    symbol: security?.DisplaySymbol ?? deal.Symbol,
    directionLabel: side,
    directionColor: side === 'Lend' ? 'colorTextPositive' : 'colorTextNegative',
    amount: deal.DealInfo.LoanAmount,
    amountAsset: borrowedAsset,
    amountTestId: 'deal-cashflows-header-loan-amount',
    rateTerms,
    collateralAsset,
    assetLabel: 'Collateral',
  };
}

function getRepoCashflowsHeaderDisplay(
  deal: DealRepoData,
  security: OTCRepoSecurity | undefined
): CashflowsHeaderDisplay {
  const borrowedAsset =
    security?.ProductDetails.BorrowedAsset || deal.DealInfo.EnteredQuantityAsset || deal.QuantityAsset;
  const collateralAsset = security?.ProductDetails.RepoAsset || deal.DealInfo.CollateralAsset || '—';
  const direction = deal.DealInfo.Direction;
  const rate = getOtcDealRateDisplayValue(deal);

  return {
    productType: formatProductType(deal.SubProductType),
    symbol: security?.DisplaySymbol ?? deal.Symbol,
    directionLabel: prettyName(direction),
    directionColor: direction === 'ReverseRepo' ? 'colorTextPositive' : 'colorTextNegative',
    amount: deal.DealInfo.PurchasePrice,
    amountAsset: borrowedAsset,
    amountTestId: 'deal-cashflows-header-repo-purchase-price',
    rateTerms: rate ? `@ ${rate.value} ${rate.currency}` : '—',
    collateralAsset,
    assetLabel: 'Repo Asset',
  };
}

function getFixedRateTerms(deal: DealFixedRateLoanData): string {
  const rate = getOtcDealRateDisplayValue(deal);
  return rate ? `@ ${rate.value} ${rate.currency}` : '—';
}

function getFloatingRateTerms(
  deal: DealFloatingRateLoanData,
  security: OTCFloatingRateLoanSecurity | undefined
): string {
  const rateSource = deal.DealInfo.RateSource?.trim() || security?.ProductDetails.RateSource?.trim() || '—';
  const spread = getOtcDealSpreadDisplayValue(deal);
  return spread ? `${rateSource} + ${spread.value} ${spread.currency}` : rateSource;
}

function getSubAccountDisplay(
  deal: CashflowsHeaderDeal,
  subAccountsByName: ReturnType<typeof useSubAccounts>['subAccountsByName']
): string {
  const allocations = deal.Allocation?.Allocations.map(allocation => ({
    subAccount: allocation.SubAccount,
    value: allocation.Value,
  }));

  if (allocations?.length) {
    return prettifyAllocations(allocations, deal.Allocation?.ValueType, deal.Allocation?.Currency, subAccountsByName);
  }

  return prettifySubAccount(deal.SubAccount, subAccountsByName) ?? '—';
}
